import { useQuery } from '@tanstack/react-query';
import type { MouseEvent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex, Flex } from '@/components/base';
import GridTopList from '@/components/GridList/GridTopList';
import { MainText } from '@/components/Typography/NamedTypo';
import queryKeys from '@/configs/react-query-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { ACCOUNT_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';

const { ContentBox, ContentHeader, DownloadButton } = SearchContentStyles;

type SelectedAccount = {
  aliasList: string[];
  name: string;
} | null;

const RelatedAccountsContainer = () => {
  const { alias, searchResult } = useSearchDetailContext();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { toDetailPage, refreshHistoryList } = useGlobalSearchModalAction();

  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<SelectedAccount>(null);

  const { data, isLoading } = useQuery({
    queryFn: () => service.globalSearch.fetchRelatedAccountList(alias),
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT_ACCOUNTS,
      alias,
    ],
  });

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    const filterPayload = {
      [ACCOUNT_COLUMNS.ALIAS]: [
        {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: modalState.aliasList,
        },
      ],
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [modalState.name],
      options: {
        queryKeys: [modalState.name],
        title: 'Feedback list',
        subTitle: `Account: ${modalState.name}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus?.sortBy,
                order: sortStatus?.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();
    if (!modalState) return;

    const filter = new SFilter(searchResult.filter);
    filter.addValueSet(ACCOUNT_COLUMNS.ALIAS, {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: modalState.aliasList,
    });

    const res = await service.globalSearch.submitSearch({
      title: `${searchResult.title} copy`,
      query: searchResult.query,
      filter: filter.get(),
      options: {
        ...searchResult.options,
      },
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  const onClickHandler = (
    e: MouseEvent,
    bucket: DataBucketT & { aliasList?: string[] },
  ) => {
    setPos({
      top: e.clientY,
      left: e.clientX,
    });
    setModalState({
      aliasList: bucket.aliasList || [],
      name: bucket.bucket.toString(),
    });
    open();
  };

  return (
    <ContentBox f={1} padding="24px" g={24}>
      <Flex j="space-between" a="center">
        <ContentHeader>Related accounts</ContentHeader>
        <DownloadButton onClick={() => {}} />
      </Flex>
      <ErrorBoundary
        fallback={
          <CenterFlex style={{ height: 250 }}>
            <MainText dim>Error occurred</MainText>
          </CenterFlex>
        }
      >
        <GridTopList
          data={data?.buckets}
          isLoading={isLoading}
          onClickItem={(e, bucket) => onClickHandler(e, bucket)}
        />
        <SearchDrilldownMenu
          pos={{
            top: pos?.top as number,
            left: pos?.left as number,
          }}
          status={status}
          close={close}
          onViewFeedbackListClick={onViewFeedbackListHandler}
          onAddToFilterClick={onAddToFilterHandler}
          onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
        />
      </ErrorBoundary>
    </ContentBox>
  );
};

export default RelatedAccountsContainer;
